import debounce from 'lodash/debounce'
import emitter from '../../mixin/emitter'

export default {
  name: 'UiFormTime',

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  mixins: [emitter],

  inheritAttrs: false,

  inject: {
    uiFormItem: {
      default: '',
    },
  },

  // language=HTML
  template: `
    <div class="ng-time-fields">
      <template v-if="inputMode === 'input'">
        <input type="time" v-model="textValue"  />
      </template>
      <template v-if="inputMode === 'select'">
        <ui-form-select v-model="hour" :options="hourDropdownOptions" />
        <ui-form-select v-model="minute" :options="minuteDropdownOptions" />
        <ui-form-select v-model="ampm" :options="ampmDropdownOptions" />
      </template>
    </div>
  `,

  props: {
    // Can be 12 hour or 24 hour. 04:30, 4:30, 8:30 am, 19:30
    modelValue: {
      type: String,
      required: true,
    },

    inputMode: {
      type: String,
      required: false,
      default: 'select',
    },

    minuteOptions: {
      type: Array,
      default() {
        return Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'))
      },
    },
  },

  data() {
    return {
      hour: null,
      minute: null,
      ampm: null,
    }
  },

  computed: {
    timeIn24HourFormat() {
      if (this.hour === null || this.minute === null || this.ampm === null) {
        return ''
      }

      const hour = parseInt(this.hour, 10) || 12
      const minute = parseInt(this.minute, 10) || 0
      const ampm = this.ampm || 'PM'
      let hour24

      if (ampm === 'PM' && hour < 12) {
        hour24 = (hour + 12).toString().padStart(2, '0')
      } else if (ampm === 'AM' && hour === 12) {
        hour24 = '00'
      } else {
        hour24 = hour.toString().padStart(2, '0')
      }

      const minuteStr = minute.toString().padStart(2, '0')
      return `${hour24}:${minuteStr}`
    },

    textValue: {
      get() {
        return this.timeIn24HourFormat
      },
      set(value) {
        this.emitInput(value)
      },
    },

    hourDropdownOptions() {
      return ['', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hour) => ({ value: hour, label: hour }))
    },
    minuteDropdownOptions() {
      return this.minuteOptions.map((minute) => ({
        value: minute === '' ? '' : `0${minute}`.slice(-2),
        label: minute === '' ? '' : `0${minute}`.slice(-2),
      }))
    },
    ampmDropdownOptions() {
      return ['', 'AM', 'PM'].map((str) => ({ value: str, label: str }))
    },
  },

  watch: {
    hour() {
      this.$nextTick(() => {
        this.doDebouncedEmitInput()
      })
    },
    minute() {
      this.$nextTick(() => {
        this.doDebouncedEmitInput()
      })
    },
    ampm() {
      this.$nextTick(() => {
        this.doDebouncedEmitInput()
      })
    },
  },

  methods: {
    doDebouncedEmitInput: debounce(function emit() {
      this.emitInput()
    }, 100),

    emitInput(value) {
      this.$emit('update:modelValue', value || this.timeIn24HourFormat)
    },

    loadRawTime(rawTime) {
      if (!rawTime) {
        return
      }
      if (this.tryParse12HourFormat(rawTime)) {
        return
      }
      if (this.tryParse24HourFormat(rawTime)) {
        return
      }
      throw new Error(`Failed to parse time: ${rawTime}`)
    },

    tryParse12HourFormat(rawTime) {
      const timeRegex = /^(\d{1,2}):(\d{2})\s*([apAP][mM])$/
      return this.tryParseTimeFormat(rawTime, timeRegex, true)
    },

    tryParse24HourFormat(rawTime) {
      const timeRegex = /^(\d{1,2}):(\d{2})$/
      return this.tryParseTimeFormat(rawTime, timeRegex, false)
    },

    tryParseTimeFormat(rawTime, timeRegex, is12HourFormat) {
      const match = rawTime.match(timeRegex)
      if (!match) return false

      let hours = parseInt(match[1], 10)
      let minutes = parseInt(match[2], 10)

      if (is12HourFormat) {
        const ampm = match[3].toUpperCase()
        if (ampm === 'PM' && hours < 12) {
          hours += 12
        } else if (ampm === 'AM' && hours === 12) {
          hours = 0
        }
      }

      if (is12HourFormat) {
        this.ampm = match[3].toUpperCase()
      } else {
        this.ampm = hours < 12 ? 'AM' : 'PM'
      }

      hours = hours % 12 || 12
      minutes = minutes < 10 ? `0${minutes}` : minutes

      this.hour = hours
      this.minute = minutes

      return true
    },
  },

  created() {
    this.$nextTick(() => {
      this.loadRawTime(this.modelValue)
    })
  },
}
